<template>
  <section
    class="cat-banner"
    :class="[
      `cat-banner--${tone}`,
      {
        'cat-banner--compact': compact,
        'cat-banner--full': full,
        'cat-banner--bg': !!background,
        'cat-banner--has-image': !!image && !background,
      },
    ]"
    :style="bannerStyle"
  >
    <div class="cat-banner__backdrop" aria-hidden="true">
      <span class="cat-banner__scrim"></span>
      <span class="cat-banner__orb cat-banner__orb--a"></span>
      <span class="cat-banner__orb cat-banner__orb--b"></span>
      <span class="cat-banner__grid"></span>
    </div>

    <div class="cat-banner__inner">
      <div class="cat-banner__copy">
        <div v-if="eyebrow" class="cat-banner__eyebrow">
          <span class="cat-banner__eyebrow-dot"></span>
          <span>{{ eyebrow }}</span>
        </div>
        <h2 class="cat-banner__title">
          <span class="cat-banner__title-text">{{ title }}</span>
        </h2>
        <p v-if="desc" class="cat-banner__desc">{{ desc }}</p>
        <ul v-if="displayTags.length" class="cat-banner__tags">
          <li v-for="tag in displayTags" :key="tag" class="cat-banner__tag">{{ tag }}</li>
        </ul>
        <slot />
      </div>

      <div v-if="image && !background" class="cat-banner__visual">
        <div class="cat-banner__frame" :class="{ 'cat-banner__frame--light': photoFrame === 'light' }">
          <img :src="image" :alt="title" class="cat-banner__photo" loading="lazy" />
        </div>
      </div>

      <div v-else-if="!background && !image && !full" class="cat-banner__visual">
        <div class="cat-banner__abstract" aria-hidden="true">
          <span class="cat-banner__icon-wrap">
            <svg viewBox="0 0 24 24" width="42" height="42" fill="currentColor">
              <path :d="iconPath" />
            </svg>
          </span>
          <span class="cat-banner__badge">{{ badge }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  tone: { type: String, default: 'system' },
  eyebrow: { type: String, default: '' },
  title: { type: String, required: true },
  desc: { type: String, default: '' },
  image: { type: String, default: '' },
  background: { type: String, default: '' },
  bgSize: { type: String, default: 'cover' },
  bgPosition: { type: String, default: 'center' },
  bgColor: { type: String, default: '' },
  icon: { type: String, default: 'software' },
  badge: { type: String, default: 'GAOREN' },
  tags: { type: Array, default: () => [] },
  compact: { type: Boolean, default: false },
  full: { type: Boolean, default: false },
  photoFrame: { type: String, default: 'glass' },
})

const iconPaths = {
  software:
    'M4 6h16v2H4V6zm0 5h16v2H4v-2zm0 5h16v2H4v-2z',
  education:
    'M12 3L1 9l11 6 9-4.91V17h2V9M5 13.18v4.12L12 21l7-3.7v-4.12L12 17l-7-3.82z',
  robotics:
    'M12 2a2 2 0 012 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 017 7v1h1v2h-1v1a2 2 0 01-2 2H5a2 2 0 01-2-2v-1H2v-2h1v-1a7 7 0 017-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 012-2z',
  agv:
    'M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z',
  automation:
    'M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z',
}

const iconPath = computed(() => iconPaths[props.icon] || iconPaths.software)

const bannerStyle = computed(() => {
  const style = {
    '--cat-bg-position': props.bgPosition,
    '--cat-bg-size': props.bgSize || 'cover',
  }
  if (props.background) {
    style['--cat-bg-image'] = `url("${props.background}")`
  }
  if (props.bgColor) {
    style['--cat-bg-color'] = props.bgColor
  }
  return style
})

const displayTags = computed(() => {
  if (props.tags.length) return props.tags.slice(0, 5)
  if (!props.desc) return []
  return props.desc
    .split(/[、,，/]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .slice(0, 4)
})
</script>

<style scoped>
.cat-banner {
  position: relative;
  overflow: hidden;
  border-radius: var(--radius-xl);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 24px 60px rgba(8, 22, 43, 0.18);
}

.cat-banner--compact {
  border-radius: var(--radius-lg);
  box-shadow: none;
}

.cat-banner--full {
  width: 100%;
  border-radius: 0;
  border-left: none;
  border-right: none;
  box-shadow: none;
}

.cat-banner--full.cat-banner--bg {
  min-height: 400px;
  background-color: var(--cat-bg-color, #061222);
  background-image: var(--cat-bg-image);
  background-size: var(--cat-bg-size, cover);
  background-position: var(--cat-bg-position, center);
  background-repeat: no-repeat;
}

.cat-banner__backdrop {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.cat-banner--bg .cat-banner__backdrop {
  background: transparent;
}

.cat-banner--system:not(.cat-banner--bg) .cat-banner__backdrop {
  background: linear-gradient(128deg, #061222 0%, #0c2548 55%, #0e4a62 100%);
}

.cat-banner--education:not(.cat-banner--bg) .cat-banner__backdrop {
  background: linear-gradient(128deg, #081828 0%, #0f3d28 55%, #125a72 100%);
}

.cat-banner--robotics:not(.cat-banner--bg) .cat-banner__backdrop {
  background: linear-gradient(128deg, #0a1020 0%, #1e1b6e 55%, #125a72 100%);
}

.cat-banner--agv:not(.cat-banner--bg) .cat-banner__backdrop {
  background: linear-gradient(128deg, #0a1224 0%, #143456 55%, #0d665e 100%);
}

.cat-banner--automation:not(.cat-banner--bg) .cat-banner__backdrop {
  background: linear-gradient(128deg, #0c1018 0%, #243044 55%, #1a3a9e 100%);
}

.cat-banner__scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    rgba(6, 18, 34, 0.9) 0%,
    rgba(6, 18, 34, 0.72) 28%,
    rgba(6, 18, 34, 0.38) 52%,
    rgba(6, 18, 34, 0.12) 78%,
    rgba(6, 18, 34, 0.04) 100%
  );
}

.cat-banner--system .cat-banner__scrim {
  background: linear-gradient(
    90deg,
    rgba(6, 18, 34, 0.92) 0%,
    rgba(8, 28, 52, 0.68) 30%,
    rgba(12, 48, 72, 0.32) 55%,
    rgba(14, 74, 98, 0.1) 100%
  );
}

.cat-banner--education .cat-banner__scrim {
  background: linear-gradient(
    90deg,
    rgba(8, 24, 40, 0.92) 0%,
    rgba(10, 40, 32, 0.68) 30%,
    rgba(15, 61, 40, 0.3) 55%,
    rgba(18, 90, 114, 0.08) 100%
  );
}

.cat-banner--robotics .cat-banner__scrim {
  background: linear-gradient(
    90deg,
    rgba(10, 16, 32, 0.92) 0%,
    rgba(16, 18, 48, 0.65) 28%,
    rgba(20, 20, 60, 0.28) 50%,
    rgba(10, 16, 32, 0.06) 100%
  );
}

.cat-banner--agv .cat-banner__scrim {
  background: linear-gradient(
    90deg,
    rgba(10, 18, 36, 0.92) 0%,
    rgba(14, 40, 64, 0.68) 30%,
    rgba(13, 80, 72, 0.28) 55%,
    rgba(13, 102, 94, 0.08) 100%
  );
}

.cat-banner--automation .cat-banner__scrim {
  background: linear-gradient(
    90deg,
    rgba(12, 16, 24, 0.92) 0%,
    rgba(24, 36, 52, 0.68) 30%,
    rgba(26, 58, 158, 0.28) 55%,
    rgba(26, 58, 158, 0.08) 100%
  );
}

.cat-banner__orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(48px);
  opacity: 0.35;
}

.cat-banner__orb--a {
  width: 240px;
  height: 240px;
  top: -60px;
  right: 10%;
  background: rgba(77, 195, 255, 0.22);
}

.cat-banner__orb--b {
  width: 180px;
  height: 180px;
  bottom: -50px;
  left: 5%;
  background: rgba(47, 166, 109, 0.15);
}

.cat-banner--bg .cat-banner__grid {
  display: none;
}

.cat-banner:not(.cat-banner--bg) .cat-banner__grid {
  position: absolute;
  inset: 0;
  opacity: 0.04;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.8) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.8) 1px, transparent 1px);
  background-size: 32px 32px;
}

.cat-banner__inner {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(260px, 0.85fr);
  gap: 40px;
  align-items: center;
  padding: 36px 40px;
}

.cat-banner--bg .cat-banner__inner {
  grid-template-columns: 1fr;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 56px clamp(32px, 5vw, 80px) 52px;
  min-height: 400px;
  align-content: center;
  box-sizing: border-box;
}

.cat-banner--compact .cat-banner__inner {
  padding: 28px 32px;
  gap: 24px;
}

.cat-banner--full:not(.cat-banner--bg) .cat-banner__inner {
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 52px clamp(24px, 4vw, 72px) 48px;
  gap: clamp(24px, 5vw, 72px);
  grid-template-columns: minmax(0, 1.05fr) minmax(340px, 0.95fr);
}

.cat-banner__copy {
  color: #fff;
  min-width: 0;
  max-width: 640px;
}

.cat-banner--bg .cat-banner__copy {
  max-width: min(560px, 48%);
}

.cat-banner__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 6px 14px 6px 10px;
  border-radius: var(--radius-pill);
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.16);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.8px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(8px);
}

.cat-banner__eyebrow-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #4dc3ff;
  box-shadow: 0 0 8px rgba(77, 195, 255, 0.8);
  animation: cat-banner-pulse 2.4s ease-in-out infinite;
}

@keyframes cat-banner-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(0.85); }
}

.cat-banner__title {
  margin: 0;
  font-size: 32px;
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -0.5px;
}

.cat-banner__title-text {
  background: linear-gradient(135deg, #ffffff 30%, rgba(180, 220, 255, 0.95) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.cat-banner--compact .cat-banner__title {
  font-size: 24px;
}

.cat-banner--full .cat-banner__title {
  font-size: clamp(32px, 3.6vw, 44px);
  letter-spacing: -0.8px;
}

.cat-banner__desc {
  margin: 16px 0 0;
  max-width: 520px;
  font-size: 14px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.75);
}

.cat-banner--full .cat-banner__desc {
  font-size: 15px;
  line-height: 1.75;
  color: rgba(255, 255, 255, 0.7);
}

.cat-banner__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 22px 0 0;
  padding: 0;
  list-style: none;
}

.cat-banner__tag {
  padding: 6px 14px;
  border-radius: var(--radius-pill);
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.14);
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  letter-spacing: 0.2px;
  backdrop-filter: blur(6px);
}

.cat-banner__visual {
  display: flex;
  justify-content: center;
  align-items: center;
}

.cat-banner__frame {
  width: 100%;
  max-width: 360px;
  min-height: 200px;
  padding: 18px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.cat-banner__frame--light {
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid rgba(255, 255, 255, 0.5);
  backdrop-filter: none;
  box-shadow: 0 18px 42px rgba(8, 22, 43, 0.22);
}

.cat-banner__photo {
  width: 100%;
  max-height: 200px;
  object-fit: contain;
  filter: drop-shadow(0 16px 28px rgba(0, 0, 0, 0.28));
}

.cat-banner__frame--light .cat-banner__photo {
  filter: none;
}

.cat-banner__abstract {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  color: rgba(255, 255, 255, 0.9);
}

.cat-banner__icon-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 76px;
  height: 76px;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
}

.cat-banner__badge {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 2px;
  color: rgba(255, 255, 255, 0.72);
}

@media (max-width: 900px) {
  .cat-banner--full.cat-banner--bg {
    min-height: 340px;
  }

  .cat-banner--bg .cat-banner__inner {
    padding: 40px 28px 36px;
    min-height: 340px;
  }

  .cat-banner--bg .cat-banner__copy {
    max-width: 100%;
  }

  .cat-banner__inner {
    grid-template-columns: 1fr;
    padding: 28px 24px;
    gap: 28px;
  }

  .cat-banner__title {
    font-size: 26px;
  }
}
</style>
